import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

import {NameListSubject} from '../../../../modes/attendance/attendanceNameList'

const INITIAL_VISIBLE_NAMES = 12
const LOAD_MORE_BATCH = 24

type AttendanceV2NameListSectionProps = {
  names: string[]
  theme: 'light' | 'dark'
  /** Heading without the count — the column label the list came out of. */
  title?: string
  /** Who the names are, when the list was lifted out of prose and has no column label. */
  subject?: NameListSubject
}

/** A list of names the backend had packed into one cell or one sentence, drawn as a numbered
 *  multi-column list with the same load-more batching the tables use. Fifty names in a paragraph
 *  are unscannable; fifty names in columns of twelve are a roster. */
const AttendanceV2NameListSection: React.FC<AttendanceV2NameListSectionProps> = ({
  names,
  theme,
  title,
  subject,
}) => {
  const intl = useIntl()
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_NAMES)

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_NAMES)
  }, [names.length])

  const visibleNames = useMemo(() => names.slice(0, visibleCount), [names, visibleCount])
  const showLoadMore = names.length > INITIAL_VISIBLE_NAMES && visibleCount < names.length
  const remaining = names.length - visibleCount
  const showLess = visibleCount > INITIAL_VISIBLE_NAMES
  const collapsible = Math.min(
    LOAD_MORE_BATCH,
    Math.min(visibleCount, names.length) - INITIAL_VISIBLE_NAMES
  )

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + LOAD_MORE_BATCH, names.length))
  }, [names.length])

  const handleShowLess = useCallback(() => {
    setVisibleCount((prev) =>
      Math.max(Math.min(prev, names.length) - LOAD_MORE_BATCH, INITIAL_VISIBLE_NAMES)
    )
  }, [names.length])

  if (!names.length) return null

  const isDark = theme === 'dark'
  const borderColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'
  const headerBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)'
  const textColor = isDark ? '#ffffff' : '#212529'
  const mutedColor = isDark ? 'rgba(255,255,255,0.65)' : '#6c757d'

  const heading = title
    ? intl.formatMessage(
        {id: 'CHATBOT.ATTENDANCE.NAME_LIST_COLUMN_TITLE'},
        {title, count: names.length}
      )
    : intl.formatMessage(
        {id: 'CHATBOT.ATTENDANCE.NAME_LIST_TITLE'},
        {subject: subject ?? 'other', count: names.length}
      )

  return (
    <div className='mt-2' style={{borderRadius: 8, border: `1px solid ${borderColor}`}}>
      <div
        className='px-2 py-2 fw-semibold'
        style={{
          fontSize: '0.8rem',
          color: textColor,
          backgroundColor: headerBg,
          borderBottom: `1px solid ${borderColor}`,
          borderRadius: '8px 8px 0 0',
        }}
      >
        {heading}
      </div>
      <ol
        className='list-unstyled mb-0 px-2 py-2'
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          columnGap: '0.75rem',
          rowGap: '0.15rem',
          fontSize: '0.8rem',
          color: textColor,
        }}
      >
        {visibleNames.map((name, idx) => (
          <li
            key={`${name}-${idx}`}
            className='d-flex gap-1'
            style={{minWidth: 0, lineHeight: 1.4}}
            title={name}
          >
            <span
              style={{
                color: mutedColor,
                flexShrink: 0,
                minWidth: '1.6em',
                textAlign: 'end',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {idx + 1}.
            </span>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </span>
          </li>
        ))}
      </ol>
      {showLoadMore || showLess ? (
        <div className='d-flex justify-content-end gap-1 px-2 pb-2'>
          {showLess ? (
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={handleShowLess}
              style={{fontSize: '0.75rem', whiteSpace: 'nowrap', padding: '0.35rem 0.6rem'}}
            >
              {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.SHOW_LESS'})}
              {` (${collapsible})`}
            </button>
          ) : null}
          {showLoadMore ? (
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={handleLoadMore}
              style={{fontSize: '0.75rem', whiteSpace: 'nowrap', padding: '0.35rem 0.6rem'}}
            >
              {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.LOAD_MORE'})}
              {remaining > LOAD_MORE_BATCH ? ` (${LOAD_MORE_BATCH})` : ` (${remaining})`}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default AttendanceV2NameListSection
